import { useState } from 'react';
import type { ReactElement } from 'react';

import type { Column } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, EyeOff, MoreVertical } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';

import type { NumberFilterValue } from './filterFns';

export type ColumnFilterConfig =
  | { type: 'text'; placeholder?: string }
  | { type: 'number'; placeholder?: string }
  | { type: 'select'; options: string[]; placeholder?: string }
  | { type: 'boolean'; trueLabel?: string; falseLabel?: string };

interface Props<TData, TValue> {
  column: Column<TData, TValue>;
  title: string;
  filter?: ColumnFilterConfig;
  sortable?: boolean;
}

type SingleOp = 'eq' | 'gt' | 'lt';

export const ColumnHeaderMenu = <TData, TValue>({
  column,
  title,
  filter,
  sortable = true,
}: Props<TData, TValue>): ReactElement => {
  const sortDir = column.getIsSorted();
  const filterValue = column.getFilterValue();
  const hasFilter = filterValue !== undefined && filterValue !== '' && filterValue !== null;
  const columnId = column.id;
  const canSort = sortable && column.getCanSort();

  const numberFilter = (filter?.type === 'number' ? filterValue : undefined) as NumberFilterValue | undefined;
  const [uiMode, setUiMode] = useState<'single' | 'range'>(numberFilter?.op === 'range' ? 'range' : 'single');
  const [uiOp, setUiOp] = useState<SingleOp>(numberFilter && numberFilter.op !== 'range' ? numberFilter.op : 'eq');

  const cycleSort = (): void => {
    if (sortDir === false) column.toggleSorting(false);
    else if (sortDir === 'asc') column.toggleSorting(true);
    else column.clearSorting();
  };

  const setSingle = (op: SingleOp, raw: string): void => {
    const v = raw.trim();
    column.setFilterValue(v ? { op, value: v } : undefined);
  };

  const setRange = (rawMin: string, rawMax: string): void => {
    const min = rawMin.trim();
    const max = rawMax.trim();
    if (!min && !max) {
      column.setFilterValue(undefined);
      return;
    }
    column.setFilterValue({
      op: 'range',
      min: min || undefined,
      max: max || undefined,
    });
  };

  const renderFilter = (): ReactElement | null => {
    if (!filter) return null;
    switch (filter.type) {
      case 'text':
        return (
          <Input
            placeholder={filter.placeholder ?? 'Contiene…'}
            value={(filterValue as string) ?? ''}
            onChange={(e) => column.setFilterValue(e.target.value.trim() || undefined)}
            className="h-8"
          />
        );
      case 'number': {
        const singleValue = numberFilter && numberFilter.op !== 'range' ? numberFilter.value ?? '' : '';
        const rangeMin = numberFilter?.op === 'range' ? numberFilter.min ?? '' : '';
        const rangeMax = numberFilter?.op === 'range' ? numberFilter.max ?? '' : '';
        return (
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-1">
              <Button
                variant={uiMode === 'single' ? 'default' : 'outline'}
                size="sm"
                className="h-7"
                onClick={() => {
                  setUiMode('single');
                  if (numberFilter?.op === 'range') column.setFilterValue(undefined);
                }}
              >
                Exacto
              </Button>
              <Button
                variant={uiMode === 'range' ? 'default' : 'outline'}
                size="sm"
                className="h-7"
                onClick={() => {
                  setUiMode('range');
                  if (numberFilter && numberFilter.op !== 'range') column.setFilterValue(undefined);
                }}
              >
                Rango
              </Button>
            </div>
            {uiMode === 'single' ? (
              <>
                <Select
                  value={uiOp}
                  onValueChange={(v) => {
                    const nextOp = v as SingleOp;
                    setUiOp(nextOp);
                    if (singleValue) setSingle(nextOp, singleValue);
                  }}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eq">Igual a</SelectItem>
                    <SelectItem value="gt">Mayor a</SelectItem>
                    <SelectItem value="lt">Menor a</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  placeholder={filter.placeholder ?? 'Valor'}
                  value={singleValue}
                  onChange={(e) => setSingle(uiOp, e.target.value)}
                  className="h-8"
                />
              </>
            ) : (
              <>
                <Input
                  type="number"
                  placeholder="Desde"
                  value={rangeMin}
                  onChange={(e) => setRange(e.target.value, rangeMax)}
                  className="h-8"
                />
                <Input
                  type="number"
                  placeholder="Hasta"
                  value={rangeMax}
                  onChange={(e) => setRange(rangeMin, e.target.value)}
                  className="h-8"
                />
              </>
            )}
          </div>
        );
      }
      case 'select':
        return (
          <Select
            value={(filterValue as string) ?? '__all__'}
            onValueChange={(v) => column.setFilterValue(v === '__all__' ? undefined : v)}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder={filter.placeholder ?? 'Todos'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {filter.options.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'boolean': {
        const trueLabel = filter.trueLabel ?? 'Sí';
        const falseLabel = filter.falseLabel ?? 'No';
        const current = filterValue === true ? 'true' : filterValue === false ? 'false' : 'all';
        return (
          <RadioGroup
            value={current}
            onValueChange={(v) => column.setFilterValue(v === 'all' ? undefined : v === 'true')}
            className="flex flex-col gap-1.5"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="all" id={`${columnId}-all`} />
              <Label htmlFor={`${columnId}-all`} className="text-sm font-normal">
                Todos
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="true" id={`${columnId}-true`} />
              <Label htmlFor={`${columnId}-true`} className="text-sm font-normal">
                {trueLabel}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="false" id={`${columnId}-false`} />
              <Label htmlFor={`${columnId}-false`} className="text-sm font-normal">
                {falseLabel}
              </Label>
            </div>
          </RadioGroup>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="flex items-center gap-1">
      {canSort ? (
        <button
          type="button"
          onClick={cycleSort}
          className="flex items-center gap-1 text-sm font-semibold text-text-default outline-none transition-colors hover:text-brand-vivid focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          {title}
          {sortDir === 'asc' && <ArrowUp className="size-3 text-brand-vivid" />}
          {sortDir === 'desc' && <ArrowDown className="size-3 text-brand-vivid" />}
        </button>
      ) : (
        <span className="text-sm font-semibold">{title}</span>
      )}
      <Popover>
        <PopoverTrigger
          type="button"
          aria-label={`Opciones de ${title}`}
          className="relative ml-auto inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-brand-navy outline-none hover:bg-action-ghost-hover focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <MoreVertical className="size-3.5" />
          {hasFilter && <span className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-brand-vivid" />}
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-2">
          {sortable && (
            <div className="flex flex-col gap-0.5">
              <p className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-subtle">
                Ordenar
              </p>
              <Button
                variant="ghost"
                size="sm"
                className={`justify-start h-8 ${sortDir === 'asc' ? 'bg-accent' : ''}`}
                onClick={() => column.toggleSorting(false)}
              >
                <ArrowUp className="size-3.5 mr-2" />
                Ascendente
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`justify-start h-8 ${sortDir === 'desc' ? 'bg-accent' : ''}`}
                onClick={() => column.toggleSorting(true)}
              >
                <ArrowDown className="size-3.5 mr-2" />
                Descendente
              </Button>
              {sortDir && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start h-8 text-text-subtle"
                  onClick={() => column.clearSorting()}
                >
                  Quitar orden
                </Button>
              )}
            </div>
          )}
          {sortable && filter && <Separator className="my-2" />}
          {filter && (
            <div className="flex flex-col gap-2">
              <p className="px-2 text-[10px] font-semibold uppercase tracking-wider text-text-subtle">Filtrar</p>
              <div className="px-2">{renderFilter()}</div>
              {hasFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start h-8 text-text-subtle"
                  onClick={() => column.setFilterValue(undefined)}
                >
                  Limpiar filtro
                </Button>
              )}
            </div>
          )}
          {column.getCanHide() && (
            <>
              {(sortable || filter) && <Separator className="my-2" />}
              <Button
                variant="ghost"
                size="sm"
                className="justify-start h-8 text-text-subtle"
                onClick={() => column.toggleVisibility(false)}
              >
                <EyeOff className="size-3.5 mr-2" />
                Ocultar columna
              </Button>
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
